import type { RouteLocationRaw } from 'vue-router';

import type { PlaybookCollection } from '@/composables/usePlaybook';

export type KnowledgeDomainKey = PlaybookCollection | 'needs-review';

export interface KnowledgeDomain {
  articleRouteName: string;
  baseRouteName: string;
  defaultRouteName: string;
  icon: string;
  key: KnowledgeDomainKey;
  label: string;
  navRoutes: string[];
  newRouteName: string;
  proposalRouteName: string;
}

export const PLAYBOOK_DOMAINS: KnowledgeDomain[] = [
  {
    key: 'collections',
    label: 'Collections',
    icon: 'fa-album-collection-circle-plus',
    baseRouteName: 'playbook.collection',
    defaultRouteName: 'playbook.collection.needs-review',
    articleRouteName: 'playbook.collection.knowledge.article',
    newRouteName: 'playbook.collection.knowledge.new',
    proposalRouteName: 'playbook.collection.proposal',
    navRoutes: [
      'playbook.collection.needs-review',
      'playbook.collection.dunning-states',
      'playbook.collection.call-guidelines',
      'playbook.collection.segmentation',
      'playbook.collection.settings',
    ],
  },
  {
    key: 'cash_application',
    label: 'Cash Application',
    icon: 'fa-money-bill-transfer',
    baseRouteName: 'playbook.cash-app',
    defaultRouteName: 'playbook.cash-app.needs-review',
    articleRouteName: 'playbook.cash-app.knowledge.article',
    newRouteName: 'playbook.cash-app.knowledge.new',
    proposalRouteName: 'playbook.cash-app.proposal',
    navRoutes: [
      'playbook.cash-app.needs-review',
      'playbook.cash-app.adjustments',
      'playbook.cash-app.permissions',
      'playbook.cash-app.guidelines',
    ],
  },
  {
    key: 'disputes',
    label: 'Disputes',
    icon: 'fa-balance-scale',
    baseRouteName: 'playbook.disputes',
    defaultRouteName: 'playbook.disputes.needs-review',
    articleRouteName: 'playbook.disputes.knowledge.article',
    newRouteName: 'playbook.disputes.knowledge.new',
    proposalRouteName: 'playbook.disputes.proposal',
    navRoutes: [
      'playbook.disputes.needs-review',
      'playbook.disputes.codes',
      'playbook.disputes.outreach-pause',
    ],
  },
  {
    key: 'credit_risk',
    label: 'Credit Risk',
    icon: 'fa-chart-line',
    baseRouteName: 'playbook.credit',
    defaultRouteName: 'playbook.credit.needs-review',
    articleRouteName: 'playbook.credit.knowledge.article',
    newRouteName: 'playbook.credit.knowledge.new',
    proposalRouteName: 'playbook.credit.proposal',
    navRoutes: ['playbook.credit.needs-review'],
  },
  {
    key: 'deductions',
    label: 'Deductions',
    icon: 'fa-percentage',
    baseRouteName: 'playbook.deductions',
    defaultRouteName: 'playbook.deductions.agent-knowledge',
    articleRouteName: 'playbook.deductions.knowledge.article',
    newRouteName: 'playbook.deductions.knowledge.new',
    proposalRouteName: 'playbook.deductions.knowledge.article',
    navRoutes: ['playbook.deductions.agent-knowledge'],
  },
  {
    key: 'company',
    label: 'Company',
    icon: 'fa-buildings',
    baseRouteName: 'playbook.company',
    defaultRouteName: 'playbook.company.needs-review',
    articleRouteName: 'playbook.company.knowledge.article',
    newRouteName: 'playbook.company.knowledge.new',
    proposalRouteName: 'playbook.company.proposal',
    navRoutes: [
      'playbook.company.needs-review',
      'playbook.company.w9-form',
      'playbook.company.ach-document',
    ],
  },
  {
    key: 'payments',
    label: 'Payments',
    icon: 'fa-sack-dollar',
    baseRouteName: 'playbook.payments',
    defaultRouteName: 'playbook.payments.needs-review',
    articleRouteName: 'playbook.payments.knowledge.article',
    newRouteName: 'playbook.payments.knowledge.new',
    proposalRouteName: 'playbook.payments.proposal',
    navRoutes: ['playbook.payments.needs-review'],
  },
];

export function getPlaybookDomain(
  collection: PlaybookCollection | string | null | undefined,
): KnowledgeDomain | undefined {
  if (!collection) return undefined;
  return PLAYBOOK_DOMAINS.find((d) => d.key === collection);
}

export function getPlaybookDomainByBaseRoute(
  baseRouteName: string | null | undefined,
): KnowledgeDomain | undefined {
  if (!baseRouteName) return undefined;
  return PLAYBOOK_DOMAINS.find((d) => d.baseRouteName === baseRouteName);
}

export function getArticleRouteLocation(
  collection: PlaybookCollection | string | null | undefined,
  articleId: string | null | undefined,
): RouteLocationRaw | undefined {
  const domain = getPlaybookDomain(collection);
  if (!domain || !articleId) return undefined;
  return { name: domain.articleRouteName, params: { articleId } };
}
